import React, { useState, useRef, useEffect, useCallback, useMemo, FormEvent } from "react";
import { ArrowLeft, Paperclip, ArrowUp, Loader2, Mic, Play, Pause, ShoppingCart, Plus, Minus, Check, X, Link, Package, Reply } from "lucide-react";
// ============ FRAMER MOTION IMPORTS - START ============
import { motion } from "framer-motion";
// ============ FRAMER MOTION IMPORTS - END ============
import { VisitorInfo } from "@/shared/types/chat-widget-visitor";
import { ASSET_BASE } from "../lib/asset-base";
import { UserInfoForm, UserInfoFormValues } from "./UserInfoForm";
import { UserInfoConfig } from "@/lib/quick-actions";
import { getContactInfo } from "@/utils/contact-management";
import { ChatEngine, Message, QuotedMessage, replyKeyFor } from "@/lib/chat-engine";
import { getMessageDisplayText, getMessageButtonMeta, formatWhatsAppText } from "@/utils/message-parser";
import { EmojiPickerButton } from "./EmojiPickerButton";
import { supabase } from "@/integrations/supabase/client";
import TwoFactorForm from "./TwoFactorForm";

// The MP3 encoder wasm is fetched at runtime (voice notes are rare) instead of
// shipping ~180KB of base64 wasm inside the widget bundle. Served from the
// widget's own Vercel deployment with CORS enabled (see vercel.json).
const MP3_WASM_URL = `${ASSET_BASE}/mp3.enc.wasm`;

// ── Voice Note Player ──────────────────────────────────────────────────────
const WAVEFORM_HEIGHTS = [3, 5, 8, 5, 10, 7, 4, 9, 6, 5, 8, 4, 7, 5, 3, 6, 4, 8, 5, 3];

function VoiceNotePlayer({ mediaUrl, isUser }: { mediaUrl: string; isUser: boolean }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleToggle = useCallback(async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    setIsLoadingAudio(true);
    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (err) {
      console.error('Voice note playback error:', err);
    } finally {
      setIsLoadingAudio(false);
    }
  }, [isPlaying]);

  useEffect(() => {
    const audio = new Audio(mediaUrl);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => setDuration(audio.duration ?? 0);
    audioRef.current = audio;
    audio.onended = () => { setIsPlaying(false); setCurrentTime(0); };
    audio.ontimeupdate = () => setCurrentTime(audio.currentTime ?? 0);
    return () => { audio.pause(); };
  }, [mediaUrl]);

  const fmt = (s: number) => {
    if (!isFinite(s) || s === 0) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="flex items-center gap-2 py-0.5 min-w-[160px]">
      <button
        onClick={handleToggle}
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
          isUser ? 'bg-white/20 hover:bg-white/30' : 'bg-black/10 hover:bg-black/20'
        }`}
      >
        {isLoadingAudio ? (
          <Loader2 size={12} className="animate-spin" />
        ) : isPlaying ? (
          <Pause size={12} fill="currentColor" />
        ) : (
          <Play size={12} fill="currentColor" className="ml-0.5" />
        )}
      </button>

      <div className="flex items-end gap-[2px] h-6 flex-1">
        {WAVEFORM_HEIGHTS.map((h, i) => {
          const barProgress = i / WAVEFORM_HEIGHTS.length;
          const isActive = barProgress <= progress && isPlaying;
          return (
            <div
              key={i}
              className={`w-[2px] rounded-full transition-colors ${
                isUser
                  ? isActive ? 'bg-white' : 'bg-white/40'
                  : isActive ? 'bg-gray-700' : 'bg-gray-400'
              }`}
              style={{ height: `${h}px` }}
            />
          );
        })}
      </div>

      <span className={`text-[10px] flex-shrink-0 tabular-nums ${isUser ? 'text-white/70' : 'text-gray-500'}`}>
        {fmt(isPlaying ? currentTime : duration)}
      </span>
    </div>
  );
}
// ── End Voice Note Player ───────────────────────────────────────────────────

interface ChatSessionProps {
  organizationId: string;
  widgetId: string;
  visitorInfo: VisitorInfo;
  contactId: string | null;
  userInfo: UserInfoFormValues | null;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  onBack: () => void;
  onSaveContactId: (contactId: string) => void;
  /** Visitor's ISO2 country (IP geo) — regional phone-selector default. */
  visitorCountry?: string | null;
  theme: "light" | "dark";
  customIcons?: {
    userWeb?: React.ReactNode;
    botWeb?: React.ReactNode;
    humanWeb?: React.ReactNode;
  };
  customColors?: {
    backgroundColor?: string;
    textColor?: string;
    secondaryTextColor?: string;
    borderColor?: string;
    inputBackgroundColor?: string;
    buttonBackgroundColor?: string;
    buttonTextColor?: string;
  };
  // ============ FRAMER MOTION: enableAnimations prop - START ============
  enableAnimations?: boolean;
  // ============ FRAMER MOTION: enableAnimations prop - END ============
  agentName?: string;
  twoFa?: boolean;
  shopifyWebsite?: string | null;
  storefront?: boolean;
  hasOrders?: boolean;
  onViewOrders?: () => void;
  // Starter message (centre-mode chip / "Ask" bar, or a home-screen quick
  // action in either mode). Fired once after the chat is open and its history
  // has loaded (see effect below).
  pendingMessage?: string;
  // "send" (default) fires the message immediately; "prefill" only seeds the
  // input so the visitor reviews and presses send themselves.
  pendingMode?: "send" | "prefill";
  onPendingSent?: () => void;
  // Standalone full-page mode (the /c/:token WhatsApp deep-link page): there
  // is no home screen to go back to, so the floating back chip is hidden.
  hideBackButton?: boolean;
  /** Pre-chat contact form rules + copy (web_chat_widgets.user_info).
   *  Null/absent = the historical form (email optional, Skip shown). */
  userInfoConfig?: UserInfoConfig | null;
}

const ChatSession: React.FC<ChatSessionProps> = ({
  organizationId,
  widgetId,
  visitorInfo,
  contactId,
  userInfo: initialUserInfo,
  messages,
  setMessages,
  onBack,
  onSaveContactId,
  visitorCountry = null,
  theme,
  customIcons,
  customColors = {},
  // ============ FRAMER MOTION: Default enableAnimations to true - START ============
  enableAnimations = true,
  // ============ FRAMER MOTION: Default enableAnimations to true - END ============
  agentName = "AI Agent",
  twoFa = false,
  shopifyWebsite: shopifyWebsiteProp = null,
  storefront = false,
  hasOrders = false,
  onViewOrders,
  pendingMessage,
  pendingMode = "send",
  onPendingSent,
  hideBackButton = false,
  userInfoConfig = null,
}) => {
  const [newMessage, setNewMessage] = useState("");
  // Quoted replies: the message the visitor is replying to (reply button on a
  // bubble → bar above the composer → sent as replyContextId).
  const [replyTarget, setReplyTarget] = useState<Message | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // True once the initial history load has actually finished (or there was
  // nothing to load). The centre-mode auto-send waits on THIS, not on the
  // `isLoading` flag — `isLoading` is a state value that is still false during
  // the mount commit, so a guard on it fires the send before the load and the
  // late-arriving loadMessages() then replaces (wipes) the just-sent message.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Decided at mount so the form (or the composer) is on the very first
  // frame; the sync effect below keeps it current.
  const [showUserInfoForm, setShowUserInfoForm] = useState(!contactId && !initialUserInfo);
  const [showTwoFaForm, setShowTwoFaForm] = useState(false);
  const [twoFaSending, setTwoFaSending] = useState(false);
  // Where api/two-fa actually delivered the code (it, not us, picks the channel).
  const [twoFaChannel, setTwoFaChannel] = useState<"whatsapp" | "email" | undefined>();
  const [twoFaSentTo, setTwoFaSentTo] = useState<string | undefined>();
  const [pendingContactId, setPendingContactId] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfoFormValues | null>(initialUserInfo);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [showCaptionDialog, setShowCaptionDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileCaption, setFileCaption] = useState("");
  
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [productCache, setProductCache] = useState<Record<string, { product_title: string; price: string; image_src: string | null; handle: string | null }>>({});
  const shopifyWebsite = shopifyWebsiteProp?.replace(/\/$/, '') || null;
  const [cart, setCart] = useState<Record<string, { product: any; quantity: number }>>({});
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [addingToCartId, setAddingToCartId] = useState<string | null>(null);
  const [addingToCartQty, setAddingToCartQty] = useState(1);
  const cartRef = useRef<HTMLDivElement>(null);
  const cartItemCount = Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatEngineRef = useRef<ChatEngine | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // How many messages were present when the chat first mounted. Anything at or
  // below this index is "history" — it renders instantly without the entrance
  // animation. Anything above is "new" and animates in. This is what kills the
  // slammy feeling when opening a chat with a long thread.
  const initialMessageCountRef = useRef<number | null>(null);
  if (initialMessageCountRef.current === null) {
    initialMessageCountRef.current = messages.length;
  }

  // A thread already on screen (the visitor is coming back from the home
  // screen) is shown as it is; the engine is seeded with it and only catches
  // up on what arrived while they were away. Nothing to load = no spinner.
  const threadInMemory = (initialMessageCountRef.current ?? 0) > 0;

  // History spinner: only while a load is actually running, only after a
  // short delay (a quick load must not flash one), and once shown it stays
  // long enough to read as a spinner rather than a flicker. It sits in the
  // message area; the header and composer are there from the first frame.
  const HISTORY_SPINNER_DELAY_MS = 200;
  const HISTORY_SPINNER_MIN_MS = 300;
  const [showHistorySpinner, setShowHistorySpinner] = useState(false);
  const spinnerShownAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!historyLoaded) {
      const t = setTimeout(() => {
        spinnerShownAtRef.current = Date.now();
        setShowHistorySpinner(true);
      }, HISTORY_SPINNER_DELAY_MS);
      return () => clearTimeout(t);
    }
    if (spinnerShownAtRef.current === null) return;
    const wait = Math.max(0, HISTORY_SPINNER_MIN_MS - (Date.now() - spinnerShownAtRef.current));
    const t = setTimeout(() => setShowHistorySpinner(false), wait);
    return () => clearTimeout(t);
  }, [historyLoaded]);

  // Rows delivered as a whole list before the first load settles ARE the
  // history: they render in place, without the new-message entrance.
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    historyLoadedRef.current = historyLoaded;
  }, [historyLoaded]);

  const primaryColor = customColors?.buttonBackgroundColor || "#2563eb";


  // Initialize ChatEngine ONCE per mount. Crucially `contactId` is NOT a
  // dependency: when a brand-new contact is created mid-send, recreating the
  // engine here would call loadMessages() and wholesale-replace the live message
  // list with a mid-write DB snapshot — wiping the message you just sent. Instead
  // the sync effect below feeds contactId changes through updateConfig(), which
  // re-subscribes realtime WITHOUT touching the messages array.
  const firstSyncRef = useRef(true);
  useEffect(() => {
    chatEngineRef.current = new ChatEngine({
      organizationId,
      widgetId,
      visitorInfo,
      contactId,
      userInfo,
      onContactIdChange: onSaveContactId,
      onMessagesChange: (next) => {
        if (Array.isArray(next) && !historyLoadedRef.current) {
          initialMessageCountRef.current = next.length;
        }
        setMessages(next);
      },
      onLoadingChange: setIsLoading,
      twoFa,
      knownMessages: threadInMemory ? messages : undefined,
    });

    return () => {
      chatEngineRef.current?.cleanup();
      chatEngineRef.current = null;
      firstSyncRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, widgetId]);

  // Keep the engine config in sync, and load existing history on the FIRST sync
  // after mount (returning visitor / re-entry catches up on anything that
  // arrived while away). A contactId that appears on a LATER sync means a
  // brand-new contact was just created mid-send — we updateConfig (re-subscribe
  // realtime) but must NOT reload, or loadMessages() would wholesale-replace the
  // live list and wipe the optimistic message just sent.
  useEffect(() => {
    const engine = chatEngineRef.current;
    if (!engine) return;

    engine.updateConfig({ contactId, userInfo, visitorInfo });

    const isFirstSync = firstSyncRef.current;
    firstSyncRef.current = false;

    if (isFirstSync) {
      if (contactId && !engine.hasHistoryBaseline()) {
        // Mark history loaded only when loadMessages() truly resolves, so the
        // auto-send fires AFTER the replace, never racing it.
        engine.loadMessages().finally(() => setHistoryLoaded(true));
      } else {
        // Nothing to load: no contact yet, or the thread is already on screen
        // (seeded into the engine, whose first poll catches up on the rest).
        setHistoryLoaded(true);
      }
    }
  }, [contactId, userInfo, visitorInfo]);

  // Fetch product data for shopifyProducts variant IDs
  useEffect(() => {
    const allVariantIds = messages
      .filter((m) => m.shopifyProducts)
      .flatMap((m) => m.shopifyProducts!.split(':').filter(Boolean));
    const missing = allVariantIds.filter((id) => !productCache[id]);
    if (missing.length === 0) return;
    const uniqueMissing = [...new Set(missing)];

    (async () => {
      const { data, error } = await supabase
        .from('products_all')
        .select('variant_id, product_title, price, image_src, handle')
        .eq('organization_id', organizationId)
        .in('variant_id', uniqueMissing);
      if (error || !data) return;
      setProductCache((prev) => {
        const next = { ...prev };
        data.forEach((p: any) => { next[p.variant_id] = p; });
        return next;
      });
    })();
  }, [messages, organizationId]);

  // Close cart dropdown on click outside (use shadow root to avoid event retargeting)
  useEffect(() => {
    const handler = (e: Event) => {
      if (cartRef.current && !cartRef.current.contains(e.target as Node)) setIsCartOpen(false);
    };
    const root = cartRef.current?.getRootNode() || document;
    root.addEventListener('mousedown', handler as EventListener);
    return () => root.removeEventListener('mousedown', handler as EventListener);
  }, []);

  // Stable identity (functional setState only) so the memoized message list
  // doesn't rebuild when unrelated state changes.
  const addToCart = useCallback((variantId: string, product: any, qty: number) => {
    setCart((prev) => {
      const existing = prev[variantId];
      return { ...prev, [variantId]: { product, quantity: (existing?.quantity || 0) + qty } };
    });
  }, []);

  const clearCart = () => setCart({});

  const getCartUrl = () => {
    if (!shopifyWebsite || Object.keys(cart).length === 0) return null;
    const items = Object.entries(cart).map(([vid, item]) => `${vid}:${item.quantity}`).join(',');
    // storefront=true lands the customer on the storefront cart page instead of
    // going straight to checkout (per-widget setting: web_chat_widgets.checkout_storefront).
    return `${shopifyWebsite}/cart/${items}${storefront ? '?storefront=true' : ''}`;
  };

  // Auto-focus input when not loading
  useEffect(() => {
    if (!isLoading && !showUserInfoForm) {
      inputRef.current?.focus();
    }
  }, [isLoading, showUserInfoForm]);

  // Show user info form if needed
  useEffect(() => {
    setShowUserInfoForm(!contactId && !userInfo);
  }, [contactId, userInfo]);

  const handleSendMessage = async (e: React.FormEvent | null, directMessage?: string) => {
    e?.preventDefault();

    const messageToSend = directMessage || newMessage;
    if (!messageToSend.trim() || isLoading || !chatEngineRef.current) return;

    if (!contactId && !userInfo) {
      setShowUserInfoForm(true);
      return;
    }

    setNewMessage("");
    const quoted = replyTarget;
    setReplyTarget(null);
    await chatEngineRef.current.sendMessage(messageToSend, quoted);
  };

  // Reply affordance on a bubble: quote it in the composer. Only a message
  // with a key (stored row id, or the client id of the visitor's own send)
  // can be quoted; loading placeholders never can.
  const startReply = (message: Message) => {
    if (message.isLoading || !replyKeyFor(message)) return;
    setReplyTarget(message);
    inputRef.current?.focus();
  };

  // The quoted parent is rendered above the bubble's text; clicking it jumps
  // to the parent when it is on screen (bubbles carry data-message-key).
  const jumpToMessage = (key?: string | null) => {
    if (!key) return;
    const el = document.querySelector<HTMLElement>(`[data-message-key="${CSS.escape(key)}"]`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
  };
  const quotedSenderName = (quoted: QuotedMessage) => {
    const st = quoted.senderType || "";
    if (st.startsWith("user")) return "You";
    return getSenderName(st);
  };
  const quotedSnippet = (quoted: QuotedMessage) => {
    if (quoted.mediaType === "image") return "📷 Photo";
    if (quoted.mediaType === "video") return "🎥 Video";
    if (quoted.mediaType === "audio") return "🎵 Voice note";
    if (quoted.mediaType === "document") return "📄 Document";
    const text = getMessageDisplayText(quoted.text || "").replace(/\s+/g, " ").trim();
    return text.length > 120 ? `${text.slice(0, 117)}…` : text || "Message";
  };
  const renderQuote = (quoted: QuotedMessage, onDark: boolean) => (
    <button
      type="button"
      onClick={() => jumpToMessage(quoted.id)}
      className={`mb-1.5 block w-full text-left rounded-lg border-l-2 px-2 py-1 text-xs leading-snug ${
        onDark ? "border-white/60 bg-white/15 text-white/90" : "border-gray-400 bg-black/5 text-gray-700"
      }`}
      aria-label={`Replying to ${quotedSenderName(quoted)}`}
    >
      <span className="block font-semibold">{quotedSenderName(quoted)}</span>
      <span className="block truncate">{quotedSnippet(quoted)}</span>
    </button>
  );
  // Hover-only on pointer devices, always faintly visible on touch (no hover).
  const replyButton = (message: Message, align: "left" | "right") =>
    replyKeyFor(message) ? (
      <button
        type="button"
        onClick={() => startReply(message)}
        className={`shrink-0 self-center rounded-full p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-60 transition-opacity ${
          align === "right" ? "mr-1" : "ml-1"
        }`}
        title="Reply"
        aria-label="Reply to this message"
      >
        <Reply size={14} />
      </button>
    ) : null;

  // Fire the starter message ONCE — only after the chat is open and its
  // history has finished loading (historyLoaded). Waiting for the load to
  // settle means the send just appends on top and is never overwritten by a
  // late reload. Seeding newMessage first lets an unidentified visitor's text
  // survive the contact form (the form / skip / 2FA paths send it). In
  // "prefill" mode (quick actions with send_mode prefill) the text only seeds
  // the input and the visitor presses send themselves.
  const pendingSentRef = useRef(false);
  useEffect(() => {
    if (!pendingMessage || pendingSentRef.current || !historyLoaded) return;
    pendingSentRef.current = true;
    onPendingSent?.();
    setNewMessage(pendingMessage);
    if (pendingMode !== "prefill") {
      handleSendMessage(null, pendingMessage);
    } else {
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMessage, historyLoaded]);

  const handleUserInfoSubmit = async (data: UserInfoFormValues) => {
    setUserInfo(data);

    const { saveUserInfo } = getContactInfo(organizationId);
    saveUserInfo({
      fullName: data.fullName,
      phone: data.phone,
      email: data.email || undefined,
    });

    setShowUserInfoForm(false);

    if (twoFa) {
      setShowTwoFaForm(true);
      setTwoFaSending(true);
    }

    if (chatEngineRef.current) {
      chatEngineRef.current.updateConfig({ userInfo: data });
      const result = await chatEngineRef.current.submitUserInfo(data);

      if (result.twoFaPending) {
        setPendingContactId(result.contactId);
        setTwoFaChannel(result.delivery?.channel);
        setTwoFaSentTo(result.delivery?.sentTo);
        setTwoFaSending(false);
        return;
      }
    }

    if (newMessage.trim() && chatEngineRef.current) {
      const pendingMessage = newMessage;
      setNewMessage("");
      await chatEngineRef.current.sendMessage(pendingMessage);
    }
  };

  const handleSkipUserInfo = async () => {
    setShowUserInfoForm(false);

    const anonInfo: UserInfoFormValues = { fullName: 'Web Chat User', phone: '', countryCode: '', phoneNumber: '' };
    setUserInfo(anonInfo);

    const { saveUserInfo } = getContactInfo(organizationId);
    saveUserInfo({ fullName: 'Web Chat User', phone: '' });

    if (chatEngineRef.current) {
      chatEngineRef.current.updateConfig({ userInfo: anonInfo });
      await chatEngineRef.current.createAnonymousContact();
    }

    if (newMessage.trim() && chatEngineRef.current) {
      const pendingMessage = newMessage;
      setNewMessage("");
      await chatEngineRef.current.sendMessage(pendingMessage);
    }
  };

  const handleTwoFaResend = async (channel: "whatsapp" | "email") => {
    if (!chatEngineRef.current || !pendingContactId) return;
    const delivery = await chatEngineRef.current.sendTwoFaCode(pendingContactId, channel);
    if (!delivery.ok) throw new Error("Failed to send verification code");
    setTwoFaChannel(delivery.channel);
    setTwoFaSentTo(delivery.sentTo);
  };

  const handleTwoFaVerify = async (pin: string): Promise<boolean> => {
    if (!chatEngineRef.current || !pendingContactId) return false;
    const ok = await chatEngineRef.current.verifyTwoFaPin(pendingContactId, pin);
    if (ok) {
      setShowTwoFaForm(false);
      if (newMessage.trim()) {
        const pendingMessage = newMessage;
        setNewMessage("");
        await chatEngineRef.current.sendMessage(pendingMessage);
      }
    }
    return ok;
  };

  // Stable identity for the memoized message list; the ref always points at
  // the latest handleSendMessage (which closes over per-render state).
  const sendMessageRef = useRef(handleSendMessage);
  sendMessageRef.current = handleSendMessage;
  const handleActionButtonClick = useCallback((buttonText: string) => {
    if (buttonText) {
      sendMessageRef.current(null, buttonText);
    }
  }, []);

  const handleEmojiSelect = (emoji: string) => {
    const textarea = inputRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = newMessage.slice(0, start) + emoji + newMessage.slice(end);
    setNewMessage(newValue);

    setTimeout(() => {
      textarea.focus();
      const newPos = start + emoji.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch {
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const cancelRecording = () => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream?.getTracks().forEach((t) => t.stop());
      mediaRecorderRef.current = null;
    }
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordingSeconds(0);
  };

  const sendRecording = () => {
    if (!mediaRecorderRef.current) return;
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setIsRecording(false);
    setRecordingSeconds(0);

    mediaRecorderRef.current.onstop = async () => {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current?.stream?.getTracks().forEach((t) => t.stop());
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      await uploadAndSendAudio(blob);
    };
    mediaRecorderRef.current.stop();
  };

  const uploadAndSendAudio = async (blob: Blob) => {
    try {
      setIsLoading(true);

      // Convert webm to MP3 (Whisper-compatible) using wasm-media-encoders
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const { createEncoder } = await import("wasm-media-encoders");
      const encoder = await createEncoder("audio/mpeg", MP3_WASM_URL);
      encoder.configure({
        channels: audioBuffer.numberOfChannels,
        sampleRate: audioBuffer.sampleRate,
        vbrQuality: 4.0,
      });

      const channels: Float32Array[] = [];
      for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
        channels.push(audioBuffer.getChannelData(i));
      }

      const mp3Chunks: Uint8Array[] = [];
      const chunkSize = 1152;
      for (let offset = 0; offset < audioBuffer.length; offset += chunkSize) {
        const end = Math.min(offset + chunkSize, audioBuffer.length);
        const chunkChannels = channels.map((ch) => ch.slice(offset, end));
        const mp3Chunk = encoder.encode(chunkChannels);
        if (mp3Chunk.length > 0) mp3Chunks.push(new Uint8Array(mp3Chunk));
      }
      const finalChunk = encoder.finalize();
      if (finalChunk.length > 0) mp3Chunks.push(new Uint8Array(finalChunk));

      const totalLength = mp3Chunks.reduce((sum, c) => sum + c.length, 0);
      const mp3Data = new Uint8Array(totalLength);
      let mp3Offset = 0;
      for (const chunk of mp3Chunks) {
        mp3Data.set(chunk, mp3Offset);
        mp3Offset += chunk.length;
      }

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const tempFileName = `temp/audio_${timestamp}_${randomId}.mp3`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('web-chat-widgets')
        .upload(tempFileName, mp3Data, { contentType: 'audio/mpeg', cacheControl: 'no-cache' });

      if (uploadError) throw new Error(`Failed to upload audio: ${uploadError.message}`);

      const { data: { publicUrl } } = supabase.storage
        .from('web-chat-widgets')
        .getPublicUrl(uploadData.path);

      const mediaUrl = publicUrl.replace(/https:\/\/[^.]+\.supabase\.co/, 'https://auth.flowiq.live');

      await chatEngineRef.current?.sendMedia(mediaUrl, 'audio');
    } catch (error: any) {
      console.error("Error uploading voice note:", error);
      alert(`Failed to send voice note: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fmtRecordingTime = (s: number) => {
    const m = Math.floor(s / 60);
    return `${m}:${(s % 60).toString().padStart(2, '0')}`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("File size exceeds 10MB limit");
      return;
    }

    setSelectedFile(file);
    setFileCaption("");
    setShowCaptionDialog(true);
  };

  const handleFileUploadWithCaption = async () => {
    if (!selectedFile) return;

    try {
      setIsUploadingFile(true);
      setShowCaptionDialog(false);
      setIsLoading(true);

      let mediaType: string;
      if (selectedFile.type.startsWith('image/')) mediaType = 'image';
      else if (selectedFile.type.startsWith('video/')) mediaType = 'video';
      else if (selectedFile.type.startsWith('audio/')) mediaType = 'audio';
      else mediaType = 'document';

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const fileExtension = selectedFile.name.split('.').pop() || 'bin';
      const tempFileName = `temp/${mediaType}_${timestamp}_${randomId}.${fileExtension}`;

      const fileData = new Uint8Array(await selectedFile.arrayBuffer());

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('web-chat-widgets')
        .upload(tempFileName, fileData, {
          contentType: selectedFile.type,
          cacheControl: 'no-cache'
        });

      if (uploadError) throw new Error(`Failed to upload file: ${uploadError.message}`);

      const { data: { publicUrl } } = supabase.storage
        .from('web-chat-widgets')
        .getPublicUrl(uploadData.path);

      if (!publicUrl) throw new Error('Failed to get public URL');

      const mediaUrl = publicUrl.replace(
        /https:\/\/[^.]+\.supabase\.co/,
        'https://auth.flowiq.live'
      );

      await chatEngineRef.current?.sendMedia(mediaUrl, mediaType, fileCaption.trim());

      setSelectedFile(null);
      setFileCaption("");
    } catch (error: any) {
      console.error("Error uploading file:", error);
      alert(`Failed to upload file: ${error.message}`);
    } finally {
      setIsUploadingFile(false);
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return '';
    const diffInMinutes = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60));
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getSenderName = (senderType?: string) => {
    if (senderType === 'human-web' || senderType === 'human-whatsapp') return 'Human';
    if (senderType === 'bot-web' || senderType === 'bot-whatsapp') return agentName || 'AI Agent';
    return 'Support';
  };

  // Merged-channel threads (WhatsApp deep links) interleave WhatsApp rows —
  // a small green glyph beside the sender name marks those bubbles.
  const isWhatsAppRow = (senderType?: string) => senderType?.endsWith('-whatsapp') === true;
  const WhatsAppMark = () => (
    <svg viewBox="0 0 24 24" width={11} height={11} fill="#25D366" aria-label="Sent on WhatsApp" className="shrink-0">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );

  const renderMessage = (message: Message, index: number) => {
    // Only animate messages that weren't already here on mount — history loads instantly.
    const isNew = index >= (initialMessageCountRef.current ?? 0);
    const enterProps = isNew
      ? {
          initial: { opacity: 0, y: 6 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] as any },
        }
      : {};

    const messageKey = replyKeyFor(message) || undefined;
    return (
    <motion.div key={index} className="px-4 group" data-message-key={messageKey} {...enterProps}>
      <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
        {message.isUser ? (
          <div className="flex items-end gap-1.5 max-w-[80%]">
            {replyButton(message, "right")}
            {/* Merged deep-link threads: the visitor's own WHATSAPP messages
                (user-whatsapp) carry the same green mark as agent WhatsApp
                rows; user-web bubbles stay unmarked. */}
            {isWhatsAppRow(message.senderType) && (
              <span className="shrink-0 mb-1.5" title="Sent on WhatsApp">
                <WhatsAppMark />
              </span>
            )}
          <div className="min-w-0 bg-black text-white rounded-2xl px-4 py-2.5 break-words overflow-wrap-anywhere">
            {message.replyTo && renderQuote(message.replyTo, true)}
            {message.mediaUrl && message.mediaType ? (
              <div className="space-y-2">
                {message.text?.trim() && (
                  <p className="text-sm leading-relaxed whitespace-pre-line break-words">{formatWhatsAppText(message.text)}</p>
                )}
                {message.mediaType === 'image' && (
                  <img src={message.mediaUrl} alt="Uploaded" className="max-w-full rounded-lg" style={{ maxHeight: '300px', objectFit: 'contain' }} />
                )}
                {message.mediaType === 'video' && (
                  <video src={message.mediaUrl} controls className="max-w-full rounded-lg" style={{ maxHeight: '300px' }} />
                )}
                {message.mediaType === 'audio' && (
                  <VoiceNotePlayer mediaUrl={message.mediaUrl} isUser={true} />
                )}
                {message.mediaType === 'document' && (
                  <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-sm underline hover:no-underline break-all">
                    📄 {message.text || 'Open Document'}
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm leading-relaxed whitespace-pre-line break-words">{formatWhatsAppText(message.text)}</p>
            )}
          </div>
          </div>
        ) : (
          <div className="flex max-w-[80%] items-stretch">
          <div className="min-w-0 break-words overflow-wrap-anywhere">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-xs font-semibold text-gray-900">{getSenderName(message.senderType)}</span>
              {isWhatsAppRow(message.senderType) && <WhatsAppMark />}
              <span className="text-[10px] text-gray-500">• {formatTime(message.createdAt)}</span>
            </div>
            <div className="bg-gray-100 rounded-2xl px-4 py-2.5">
              {message.replyTo && renderQuote(message.replyTo, false)}
              {message.mediaUrl && message.mediaType ? (
                <div className="space-y-2">
                  {message.text?.trim() && (
                    <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-line break-words">
                      {formatWhatsAppText(getMessageDisplayText(message.text))}
                    </p>
                  )}
                  {message.mediaType === 'image' && (
                    <img src={message.mediaUrl} alt="Image" className="max-w-full rounded-lg" style={{ maxHeight: '300px', objectFit: 'contain' }} />
                  )}
                  {message.mediaType === 'video' && (
                    <video src={message.mediaUrl} controls className="max-w-full rounded-lg" style={{ maxHeight: '300px' }} />
                  )}
                  {message.mediaType === 'audio' && (
                    <VoiceNotePlayer mediaUrl={message.mediaUrl} isUser={false} />
                  )}
                  {message.mediaType === 'document' && (
                    <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline hover:no-underline break-all">
                      📄 {message.text || 'Open Document'}
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-line break-words">
                  {formatWhatsAppText(getMessageDisplayText(message.text))}
                </p>
              )}
            </div>
            {/* Action buttons — stagger children only when this is a new message.
                Reply buttons send their text; URL buttons (WhatsApp template/
                interactive rows in merged threads) open their link. */}
            {(() => {
              const buttons = getMessageButtonMeta(message.text);
              if (!buttons || buttons.length === 0) return null;
              const containerProps = isNew
                ? {
                    initial: "hidden" as const,
                    animate: "visible" as const,
                    variants: {
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: { staggerChildren: 0.04, delayChildren: 0.08 },
                      },
                    },
                  }
                : {};
              const childProps = isNew
                ? {
                    variants: {
                      hidden: { opacity: 0, y: 4 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] as any },
                      },
                    },
                  }
                : {};
              return (
                <motion.div className="mt-2 flex flex-wrap gap-2" {...containerProps}>
                  {buttons.map((btn, btnIndex: number) => (
                    <motion.button
                      key={btnIndex}
                      onClick={() =>
                        btn.url
                          ? window.open(btn.url, "_blank", "noopener")
                          : handleActionButtonClick(btn.text)
                      }
                      className="px-3 py-1.5 text-xs font-medium bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100 hover:border-purple-300 rounded-lg inline-flex items-center gap-1"
                      style={{ transition: "background-color 150ms ease-out, border-color 150ms ease-out" }}
                      {...childProps}
                    >
                      {btn.text}
                      {btn.url && <Link size={10} className="opacity-60" />}
                    </motion.button>
                  ))}
                </motion.div>
              );
            })()}

            {/* Product cards — same pattern: stagger only when the message is new */}
            {message.shopifyProducts && message.shopifyProducts.trim() !== '' && (() => {
              const variantIds = message.shopifyProducts!.split(':').filter(Boolean);
              const products = variantIds.map((id) => productCache[id]).filter(Boolean);
              if (products.length === 0) return null;
              const containerProps = isNew
                ? {
                    initial: "hidden" as const,
                    animate: "visible" as const,
                    variants: {
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: { staggerChildren: 0.05, delayChildren: 0.1 },
                      },
                    },
                  }
                : {};
              const cardProps = isNew
                ? {
                    variants: {
                      hidden: { opacity: 0, y: 6 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] as any },
                      },
                    },
                  }
                : {};
              return (
                <motion.div
                  className="mt-2 flex gap-2 overflow-x-auto pb-1"
                  style={{ scrollbarWidth: 'thin' }}
                  {...containerProps}
                >
                  {products.map((product, pIdx) => {
                    const productUrl = shopifyWebsite && product.handle ? `${shopifyWebsite}/products/${product.handle}` : null;
                    const variantId = variantIds[pIdx];
                    const isAddingThis = addingToCartId === variantId;
                    return (
                      <motion.div key={pIdx} className="flex-shrink-0" style={{ width: '140px' }} {...cardProps}>
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          {productUrl ? (
                            <a href={productUrl} target="_blank" rel="noopener noreferrer" className="no-underline">
                              {product.image_src && (
                                <div className="w-full h-[120px] bg-gray-50 flex items-center justify-center overflow-hidden">
                                  <img src={product.image_src} alt={product.product_title} className="w-full h-full object-contain" />
                                </div>
                              )}
                              <div className="px-2 pt-2">
                                <p className="text-xs font-medium text-gray-900 leading-tight" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                  {product.product_title}
                                </p>
                                <p className="text-xs font-semibold text-gray-700 mt-1">R{product.price}</p>
                              </div>
                            </a>
                          ) : (
                            <>
                              {product.image_src && (
                                <div className="w-full h-[120px] bg-gray-50 flex items-center justify-center overflow-hidden">
                                  <img src={product.image_src} alt={product.product_title} className="w-full h-full object-contain" />
                                </div>
                              )}
                              <div className="px-2 pt-2">
                                <p className="text-xs font-medium text-gray-900 leading-tight" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                  {product.product_title}
                                </p>
                                <p className="text-xs font-semibold text-gray-700 mt-1">R{product.price}</p>
                              </div>
                            </>
                          )}
                          {/* Add to cart */}
                          <div className="px-2 pb-2 pt-1.5">
                            {isAddingThis ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => setAddingToCartQty(Math.max(1, addingToCartQty - 1))} className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className="text-xs font-medium w-5 text-center text-gray-900">{addingToCartQty}</span>
                                <button onClick={() => setAddingToCartQty(addingToCartQty + 1)} className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                                  <Plus className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => { addToCart(variantId, product, addingToCartQty); setAddingToCartId(null); setAddingToCartQty(1); }}
                                  className="ml-auto w-6 h-6 rounded bg-gray-900 hover:bg-gray-700 flex items-center justify-center transition-colors"
                                >
                                  <Check className="h-3 w-3 text-white" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setAddingToCartId(variantId); setAddingToCartQty(1); }}
                                className="w-full h-7 rounded-lg bg-gray-900 hover:bg-gray-700 text-white text-[10px] font-medium flex items-center justify-center gap-1 transition-colors"
                              >
                                <Plus className="h-3 w-3" /> Add to cart
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              );
            })()}
          </div>
          {replyButton(message, "left")}
          </div>
        )}
      </div>
    </motion.div>
    );
  };

  // Rebuild the message bubbles only when something they render actually
  // changed. Without this, every keystroke in the input re-rendered (and
  // re-parsed) the entire thread. Handlers used inside are identity-stable
  // (handleActionButtonClick / addToCart / setState setters), so they're not
  // dependencies.
  const renderedMessages = useMemo(
    () => messages.map(renderMessage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, productCache, addingToCartId, addingToCartQty, shopifyWebsite, agentName]
  );

  return (
    <div
      className="relative h-full flex flex-col bg-white overflow-hidden"
      style={{ fontSize: '16px' }}
    >
      <div className="relative h-full flex flex-col">
      {/* ============ FLOATING HEADER BUTTONS - START ============ */}
      <motion.div
        className="absolute top-3 left-3 right-3 z-20 flex items-center pointer-events-none"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      >
        {/* Back button — floating round icon (hidden in standalone full-page mode) */}
        {!hideBackButton && (
          <motion.button
            onClick={onBack}
            className="pointer-events-auto h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Back"
            aria-label="Back"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </motion.button>
        )}

        {/* Right-side floating icons — shifted left of the widget's permanent
            top-right controls (close on mobile; enlarge + close on desktop).
            Clearance: ONE 36px chip on both breakpoints (X on mobile,
            enlarge on desktop) ends 48px in; +8px gap − the container's
            12px right edge = 44px (mr-11). */}
        <div className="ml-auto flex items-center gap-2 pointer-events-auto mr-11">
          {/* My Orders */}
          {hasOrders && onViewOrders && (
            <button
              onClick={onViewOrders}
              className="h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors"
              title="My Orders"
              aria-label="My Orders"
            >
              <Package size={18} className="text-gray-700" />
            </button>
          )}

          {/* Cart */}
          {cartItemCount > 0 && (
          <div className="relative" ref={cartRef}>
            <button
              onClick={() => setIsCartOpen(!isCartOpen)}
              className="relative h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart size={18} className="text-gray-700" />
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-gray-900 text-white text-[9px] font-bold flex items-center justify-center">
                {cartItemCount}
              </span>
            </button>

            {/* Cart dropdown */}
            {isCartOpen && (
              <div onMouseDown={(e) => e.stopPropagation()} className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="max-h-60 overflow-y-auto p-3 space-y-2">
                  {Object.entries(cart).map(([vid, item]) => (
                    <div key={vid} className="flex items-center gap-2">
                      {item.product.image_src && (
                        <img src={item.product.image_src} alt="" className="w-8 h-8 rounded object-contain bg-gray-50 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{item.product.product_title}</p>
                        <p className="text-[10px] text-gray-500">R{item.product.price} x {item.quantity}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => setCart((prev) => {
                          const next = { ...prev };
                          if (next[vid].quantity <= 1) { delete next[vid]; } else { next[vid] = { ...next[vid], quantity: next[vid].quantity - 1 }; }
                          return next;
                        })} className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                          <Minus className="h-2.5 w-2.5" />
                        </button>
                        <button onClick={() => setCart((prev) => ({ ...prev, [vid]: { ...prev[vid], quantity: prev[vid].quantity + 1 } }))} className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                          <Plus className="h-2.5 w-2.5" />
                        </button>
                        <button onClick={() => setCart((prev) => { const next = { ...prev }; delete next[vid]; return next; })} className="w-5 h-5 rounded bg-gray-100 hover:bg-red-100 flex items-center justify-center text-gray-400 hover:text-red-500">
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-100 p-3 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-semibold text-gray-900">
                      R{Object.values(cart).reduce((sum, item) => sum + parseFloat(item.product.price || '0') * item.quantity, 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { const url = getCartUrl(); if (url) window.open(url, '_blank'); }}
                      className="flex-1 h-8 rounded-lg bg-gray-900 hover:bg-gray-700 text-white text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
                    >
                      Checkout
                    </button>
                    <button
                      onClick={clearCart}
                      className="h-8 px-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs text-gray-500 transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </motion.div>
      {/* ============ FRAMER MOTION: Header Slide Down - END ============ */}

      {/* Messages - Flex column-reverse so newest at bottom naturally.
          overscroll-contain: never chain scroll to the page behind. */}
      <div className="relative flex-1 overflow-y-auto overscroll-contain flex flex-col-reverse">
        {showHistorySpinner && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <svg
              className="h-7 w-7 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              aria-label="Loading"
            >
              <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="2.5" />
              <path
                d="M22 12a10 10 0 0 1-10 10"
                stroke={primaryColor}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
        <div className="space-y-4 pt-16 pb-4">
          {/* Messages */}
          {renderedMessages}
          {/* Thinking indicator — shown only for AI replies, never during initial load */}
          {isLoading && historyLoaded && (
            <motion.div
              className="px-4"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] as any }}
            >
              <div className="flex justify-start">
                <div className="max-w-[80%]">
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* 2FA Form */}
      {showTwoFaForm && (
        <div className="absolute inset-0 z-10">
          <TwoFactorForm
            onVerify={handleTwoFaVerify}
            primaryColor={customColors?.buttonBackgroundColor || primaryColor}
            theme={theme}
            isSending={twoFaSending}
            channel={twoFaChannel}
            sentTo={twoFaSentTo}
            onResend={handleTwoFaResend}
          />
        </div>
      )}

      {/* User Info Form. allow_skip false removes the Skip button entirely —
          the visitor must identify themselves before the chat starts. */}
      {showUserInfoForm && !isLoading && (
        <div className="flex-shrink-0">
          <UserInfoForm
            onSubmit={handleUserInfoSubmit}
            onSkip={
              userInfoConfig?.allow_skip === false
                ? undefined
                : handleSkipUserInfo
            }
            requireEmail={userInfoConfig?.require_email === true}
            copy={userInfoConfig?.copy}
            theme={theme}
            customColors={customColors}
            visitorCountry={visitorCountry}
          />
        </div>
      )}

      {/* Input Area — floating pill */}
      {!showUserInfoForm && !showTwoFaForm && (
        <div className="px-3 pb-3 pt-2 bg-transparent flex-shrink-0">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            accept="image/*, video/*, audio/*, application/pdf"
          />

          <form onSubmit={(e) => handleSendMessage(e)} className="relative">
            <div className="relative bg-white rounded-2xl p-3 shadow-md ring-1 ring-black/5">
              {/* Quoted reply bar: what the next send replies to. */}
              {replyTarget && (
                <div className="mb-2 flex items-start gap-2 rounded-lg border-l-2 border-gray-400 bg-gray-50 px-2 py-1.5 text-xs text-gray-700">
                  <Reply size={14} className="mt-0.5 shrink-0 text-gray-500" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">
                      Replying to {replyTarget.isUser ? "yourself" : getSenderName(replyTarget.senderType)}
                    </div>
                    <div className="truncate">
                      {quotedSnippet({ text: replyTarget.text, senderType: replyTarget.senderType, mediaType: replyTarget.mediaType ?? null })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyTarget(null)}
                    className="shrink-0 rounded-full p-0.5 text-gray-400 hover:text-gray-700"
                    aria-label="Cancel reply"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isRecording ? `Recording... ${fmtRecordingTime(recordingSeconds)}` : replyTarget ? "Type your reply..." : "Type your message..."}
                className="w-full resize-none bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none text-sm pr-12"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  } else if (e.key === "Escape" && replyTarget) {
                    setReplyTarget(null);
                  }
                }}
                disabled={isLoading || isRecording}
                rows={2}
              />

              {/* Icon row inside the pill */}
              <div className="flex gap-1 items-center pt-1">
                <EmojiPickerButton onEmojiSelect={handleEmojiSelect} disabled={isLoading || isRecording} />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingFile || isLoading || isRecording}
                  className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                  title="Upload file"
                >
                  {isUploadingFile ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
                </button>
                {isRecording ? (
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="p-1.5 text-red-400 hover:text-red-600 transition-colors"
                    title="Cancel recording"
                  >
                    <div style={{ width: 16, height: 16, borderRadius: 3, backgroundColor: 'currentColor' }} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    disabled={isLoading}
                    className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    title="Send voice note"
                  >
                    <Mic size={18} />
                  </button>
                )}
              </div>

              <div className="absolute bottom-2 right-2">
                {isRecording ? (
                  <button
                    type="button"
                    onClick={sendRecording}
                    className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-all duration-200 hover:scale-105"
                    style={{ backgroundColor: primaryColor }}
                    aria-label="Send recording"
                  >
                    <ArrowUp size={16} className="text-white" style={{ strokeWidth: 2.5 }} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || isLoading}
                    className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 disabled:hover:scale-100"
                    style={{
                      backgroundColor: newMessage.trim() && !isLoading ? primaryColor : '#e5e7eb',
                    }}
                    aria-label="Send message"
                  >
                    <ArrowUp
                      size={16}
                      className={newMessage.trim() && !isLoading ? 'text-white' : 'text-gray-400'}
                      style={{ strokeWidth: 2.5 }}
                    />
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}
      </div>

      {/* Caption Dialog */}
      {showCaptionDialog && selectedFile && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Caption (Optional)</h3>
            <p className="text-sm text-gray-600 mb-4">
              Uploading: <span className="font-medium">{selectedFile.name}</span>
            </p>
            <textarea
              value={fileCaption}
              onChange={(e) => setFileCaption(e.target.value)}
              placeholder="Add a caption..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setShowCaptionDialog(false);
                  setSelectedFile(null);
                  setFileCaption("");
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFileUploadWithCaption}
                disabled={isUploadingFile}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUploadingFile ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatSession;
